import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Facebook, 
  Instagram, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  LogOut, 
  Video, 
  AtSign, 
  Upload,
  FileText,
  ArrowRight,
  RefreshCw,
  Info,
  HelpCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MetaDemoPage = () => {
  const [searchParams] = useSearchParams();
  
  // Meta OAuth state
  const [metaAuthenticated, setMetaAuthenticated] = useState(false);
  const [metaUser, setMetaUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Pages state
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [loadingPages, setLoadingPages] = useState(false);
  
  // Instagram state
  const [instagramAccount, setInstagramAccount] = useState(null);
  const [loadingInstagram, setLoadingInstagram] = useState(false);
  
  // Post creation state
  const [postContent, setPostContent] = useState('');
  const [mentionedPages, setMentionedPages] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [postCreated, setPostCreated] = useState(false);
  const [postResult, setPostResult] = useState(null);
  const [creatingPost, setCreatingPost] = useState(false);
  
  // Video state
  const [videoTitle, setVideoTitle] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoPublished, setVideoPublished] = useState(false);
  const [videoResult, setVideoResult] = useState(null);
  const [publishingVideo, setPublishingVideo] = useState(false);
  
  // Current step for demo flow
  const [currentStep, setCurrentStep] = useState(1);

  // Handle OAuth callback params
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const name = searchParams.get('name');
    
    if (success === 'true') {
      toast.success(`Successfully connected to Facebook${name ? ` as ${name}` : ''}!`);
      // Clear URL params
      window.history.replaceState({}, '', '/meta-demo');
    } else if (error) {
      const message = searchParams.get('message') || 'Unknown error';
      toast.error(`Facebook connection failed: ${message}`);
      window.history.replaceState({}, '', '/meta-demo');
    }
  }, [searchParams]);

  // Check Meta authentication status
  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/meta/status`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setMetaAuthenticated(data.authenticated);
        setMetaUser(data.user);
        
        if (data.authenticated) {
          setCurrentStep(2);
        }
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Fetch pages when authenticated
  const fetchPages = useCallback(async () => {
    if (!metaAuthenticated) return;
    
    setLoadingPages(true);
    try {
      const response = await fetch(`${API_URL}/api/meta/pages`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPages(data.pages || []);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to fetch pages');
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Failed to fetch Facebook pages');
    } finally {
      setLoadingPages(false);
    }
  }, [metaAuthenticated]);

  useEffect(() => {
    if (metaAuthenticated && currentStep >= 2) {
      fetchPages();
    }
  }, [metaAuthenticated, currentStep, fetchPages]);

  // Fetch Instagram account
  const fetchInstagram = useCallback(async () => {
    if (!metaAuthenticated) return;
    
    setLoadingInstagram(true);
    try {
      const response = await fetch(`${API_URL}/api/meta/instagram`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setInstagramAccount(data.instagram);
      }
    } catch (error) {
      console.error('Error fetching Instagram:', error);
    } finally {
      setLoadingInstagram(false);
    }
  }, [metaAuthenticated]);

  useEffect(() => {
    if (metaAuthenticated) {
      fetchInstagram();
    }
  }, [metaAuthenticated, fetchInstagram]);

  // Select a page
  const handleSelectPage = (page) => {
    setSelectedPage(page);
    setCurrentStep(3);
    toast.success(`Page "${page.name}" selected successfully`);
  };

  // Add mention to post
  const handleAddMention = (page) => {
    if (!mentionedPages.find(p => p.id === page.id)) {
      setMentionedPages([...mentionedPages, page]);
      // Add mention format to post content
      setPostContent(prev => prev + ` @[${page.id}]`);
    }
    setShowMentionDropdown(false);
  };

  // Remove mention
  const handleRemoveMention = (pageId) => {
    setMentionedPages(mentionedPages.filter(p => p.id !== pageId));
  };

  // Create post with mentions
  const handleCreatePost = async () => {
    if (!selectedPage) {
      toast.error('Please select a page first');
      return;
    }
    
    if (!postContent.trim()) {
      toast.error('Please enter post content');
      return;
    }
    
    setCreatingPost(true);
    try {
      const response = await fetch(`${API_URL}/api/meta/post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          page_id: selectedPage.id,
          message: postContent
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPostResult({
          ...data,
          sourcePage: selectedPage.name,
          mentionedPages: mentionedPages.map(p => p.name)
        });
        setPostCreated(true);
        setCurrentStep(4);
        toast.success('Post published successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create post');
      }
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Failed to create post');
    } finally {
      setCreatingPost(false);
    }
  };

  // Publish video
  const handlePublishVideo = async () => {
    if (!selectedPage) {
      toast.error('Please select a page first');
      return;
    }
    
    if (!videoTitle.trim()) {
      toast.error('Please enter a video title');
      return;
    }
    
    setPublishingVideo(true);
    try {
      const response = await fetch(`${API_URL}/api/meta/video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          page_id: selectedPage.id,
          title: videoTitle,
          video_url: videoUrl || 'https://example.com/sample-video.mp4'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setVideoResult({
          ...data,
          pageName: selectedPage.name
        });
        setVideoPublished(true);
        setCurrentStep(5);
        toast.success('Video published successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to publish video');
      }
    } catch (error) {
      console.error('Error publishing video:', error);
      toast.error('Failed to publish video');
    } finally {
      setPublishingVideo(false);
    }
  };

  // Logout from Meta
  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/meta/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      
      setMetaAuthenticated(false);
      setMetaUser(null);
      setPages([]);
      setSelectedPage(null);
      setInstagramAccount(null);
      setPostCreated(false);
      setVideoPublished(false);
      setCurrentStep(1);
      toast.success('Logged out from Facebook');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Filtered pages for mention dropdown
  const filteredPagesForMention = pages.filter(p => 
    p.id !== selectedPage?.id &&
    !mentionedPages.find(m => m.id === p.id)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          <p className="text-gray-600">Loading Meta Integration Demo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Facebook className="h-8 w-8 text-blue-600" />
              <Instagram className="h-8 w-8 text-pink-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">AutomateSEO.ai – Meta Integration Demo</h1>
              <p className="text-sm text-gray-500">Facebook Pages & Instagram Business</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {metaAuthenticated && (
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Disconnect Facebook
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* App Introduction - REQUIRED FOR META REVIEW */}
        <div className="mb-8 bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Info className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">AutomateSEO.ai – Meta Integration Demo</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                This demo shows how <strong>AutomateSEO.ai</strong> helps businesses connect Facebook Pages and Instagram Business accounts 
                to <strong>publish posts</strong>, <strong>mention Pages</strong>, <strong>upload videos</strong>, and <strong>manage social media</strong> from one dashboard.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  <Facebook className="h-4 w-4 mr-1" /> Facebook Pages
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-pink-100 text-pink-800">
                  <Instagram className="h-4 w-4 mr-1" /> Instagram Business
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <AtSign className="h-4 w-4 mr-1" /> Page Mentions
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  <Video className="h-4 w-4 mr-1" /> Video Publishing
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* PERMISSIONS EXPLANATION - REQUIRED FOR META REVIEW */}
        <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="h-6 w-6 text-blue-600" />
            Permissions Required & How They Are Used
          </h3>
          <p className="text-gray-600 mb-4">
            When you click "Continue with Facebook", you will be asked to grant the following permissions. 
            Each permission enables a specific feature in AutomateSEO.ai:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <FileText className="h-4 w-4 text-blue-600" />
                </div>
                <span className="font-semibold text-blue-800">pages_show_list</span>
              </div>
              <p className="text-sm text-gray-600">
                <strong>What it does:</strong> Allows the app to see the list of Facebook Pages you manage.
              </p>
              <p className="text-sm text-gray-500 mt-1">
                <strong>Used in:</strong> Step 2 - Select Facebook Page
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-green-100">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <AtSign className="h-4 w-4 text-green-600" />
                </div>
                <span className="font-semibold text-green-800">pages_manage_posts</span>
              </div>
              <p className="text-sm text-gray-600">
                <strong>What it does:</strong> Allows the app to create posts on your Pages and mention other Pages.
              </p>
              <p className="text-sm text-gray-500 mt-1">
                <strong>Used in:</strong> Step 3 - Create Post with @Mention
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-purple-100">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <Video className="h-4 w-4 text-purple-600" />
                </div>
                <span className="font-semibold text-purple-800">pages_read_engagement</span>
              </div>
              <p className="text-sm text-gray-600">
                <strong>What it does:</strong> Allows the app to read engagement data and publish videos to your Pages.
              </p>
              <p className="text-sm text-gray-500 mt-1">
                <strong>Used in:</strong> Step 4 - Publish Video to Facebook Page
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-pink-100">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center">
                  <Instagram className="h-4 w-4 text-pink-600" />
                </div>
                <span className="font-semibold text-pink-800">instagram_business_basic</span>
              </div>
              <p className="text-sm text-gray-600">
                <strong>What it does:</strong> Allows the app to access and display your Instagram Business account info.
              </p>
              <p className="text-sm text-gray-500 mt-1">
                <strong>Used in:</strong> Step 5 - Instagram Business Account
              </p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2">
            {[
              { step: 1, label: 'Login with Facebook', icon: Facebook },
              { step: 2, label: 'Select Page', icon: FileText },
              { step: 3, label: 'Create Post with @Mention', icon: AtSign },
              { step: 4, label: 'Publish Video', icon: Video },
              { step: 5, label: 'Instagram Connected', icon: Instagram }
            ].map((item, index) => (
              <React.Fragment key={item.step}>
                <div className={`flex flex-col items-center ${currentStep >= item.step ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    currentStep > item.step ? 'bg-green-500 text-white' :
                    currentStep === item.step ? 'bg-blue-600 text-white' : 'bg-gray-200'
                  }`}>
                    {currentStep > item.step ? <CheckCircle className="h-6 w-6" /> : <item.icon className="h-5 w-5" />}
                  </div>
                  <span className="text-xs mt-1 text-center max-w-[80px]">{item.label}</span>
                </div>
                {index < 4 && (
                  <ArrowRight className={`h-5 w-5 ${currentStep > item.step ? 'text-green-500' : 'text-gray-300'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Step 1: Facebook Login - DIRECT LINK */}
          <Card className={`${currentStep === 1 ? 'ring-2 ring-blue-500' : ''}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Facebook className="h-5 w-5 text-blue-600" />
                Step 1: Continue with Facebook
              </CardTitle>
              <CardDescription className="flex items-center gap-1">
                <HelpCircle className="h-4 w-4" />
                Login with Facebook to authorize Pages and Instagram access
              </CardDescription>
            </CardHeader>
            <CardContent>
              {metaAuthenticated ? (
                <div className="space-y-4">
                  {/* SUCCESS CONFIRMATION */}
                  <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                    <div>
                      <p className="font-bold text-green-800 text-lg">✓ Facebook Login Successful</p>
                      <p className="text-green-700">Connected to Facebook successfully</p>
                      <p className="text-green-600 text-sm mt-1">All requested permissions granted</p>
                      {metaUser && (
                        <div className="flex items-center gap-2 mt-2">
                          {metaUser.picture && (
                            <img src={metaUser.picture} alt="" className="w-8 h-8 rounded-full" />
                          )}
                          <span className="font-medium text-green-800">{metaUser.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Permissions to be requested */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-1">
                      <Info className="h-4 w-4" />
                      Clicking this button will request the following permissions:
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1 text-blue-700">
                        <CheckCircle className="h-3 w-3" /> pages_show_list
                      </div>
                      <div className="flex items-center gap-1 text-blue-700">
                        <CheckCircle className="h-3 w-3" /> pages_manage_posts
                      </div>
                      <div className="flex items-center gap-1 text-blue-700">
                        <CheckCircle className="h-3 w-3" /> pages_read_engagement
                      </div>
                      <div className="flex items-center gap-1 text-blue-700">
                        <CheckCircle className="h-3 w-3" /> instagram_business_basic
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    {/* DIRECT LINK TO FACEBOOK OAUTH - REQUIRED FOR META REVIEW */}
                    <a href={`${API_URL}/api/meta/login`}>
                      <Button 
                        className="bg-[#1877F2] hover:bg-[#166FE5] text-white px-8 py-6 text-lg w-full"
                      >
                        <Facebook className="h-5 w-5 mr-2" /> Continue with Facebook
                      </Button>
                    </a>
                    <p className="text-xs text-gray-500 mt-3">
                      You'll see Facebook's OAuth consent screen to grant these permissions
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 2: Select Facebook Page */}
          <Card className={`${currentStep === 2 ? 'ring-2 ring-blue-500' : ''}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Step 2: Select Facebook Page
              </CardTitle>
              <CardDescription className="flex items-center gap-1">
                <HelpCircle className="h-4 w-4" />
                Select Page to manage and post content
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!metaAuthenticated ? (
                <div className="text-center py-6 text-gray-500">
                  <XCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Connect to Facebook first</p>
                </div>
              ) : loadingPages ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : pages.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <p>No Facebook Pages found</p>
                  <p className="text-sm mt-2">Make sure you have admin access to at least one Page</p>
                  <Button onClick={fetchPages} variant="outline" className="mt-4">
                    <RefreshCw className="h-4 w-4 mr-2" /> Refresh Pages
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {pages.map(page => (
                    <div 
                      key={page.id}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedPage?.id === page.id 
                          ? 'bg-blue-50 border-blue-500' 
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handleSelectPage(page)}
                    >
                      <div className="flex items-center gap-3">
                        {page.picture ? (
                          <img src={page.picture} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{page.name}</p>
                          <p className="text-xs text-gray-500">{page.category} • ID: {page.id}</p>
                        </div>
                      </div>
                      {selectedPage?.id === page.id && (
                        <CheckCircle className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* SUCCESS CONFIRMATION */}
              {selectedPage && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-green-800 font-bold flex items-center gap-2 text-lg">
                    <CheckCircle className="h-5 w-5" />
                    ✓ Page Selection Successful
                  </p>
                  <p className="text-green-700 mt-1">
                    Selected Page: <strong>{selectedPage.name}</strong>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 3: Create Post with @Mention */}
          <Card className={`${currentStep === 3 ? 'ring-2 ring-blue-500' : ''}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AtSign className="h-5 w-5 text-blue-600" />
                Step 3: Create Facebook Page Post
              </CardTitle>
              <CardDescription className="flex items-center gap-1">
                <HelpCircle className="h-4 w-4" />
                Create post with @Page mention capability
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedPage ? (
                <div className="text-center py-6 text-gray-500">
                  <XCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a Facebook Page first</p>
                </div>
              ) : postCreated ? (
                <div className="space-y-4">
                  {/* SUCCESS CONFIRMATION */}
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="font-bold text-green-800 flex items-center gap-2 text-lg">
                      <CheckCircle className="h-6 w-6" />
                      ✓ Post Published Successfully
                    </p>
                    {postResult && (
                      <div className="mt-3 space-y-2">
                        <p className="text-green-700"><strong>Source Page:</strong> {postResult.sourcePage}</p>
                        {postResult.mentionedPages?.length > 0 && (
                          <p className="text-green-700"><strong>Mentioned Pages:</strong> {postResult.mentionedPages.map(p => `@${p}`).join(', ')}</p>
                        )}
                        <div className="bg-white p-3 rounded border mt-2">
                          <p className="text-sm text-gray-600 font-medium">Post Preview:</p>
                          <p className="text-gray-800 mt-1">"{postResult.message}"</p>
                        </div>
                        <p className="text-green-600 text-sm">Post ID: {postResult.postId}</p>
                      </div>
                    )}
                  </div>
                  <Button variant="outline" onClick={() => { setPostCreated(false); setPostContent(''); setMentionedPages([]); }}>
                    Create Another Post
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700 flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Use the "@Mention Page" button to tag another Facebook Page in your post.</span>
                  </div>
                  
                  <div>
                    <Label>Post Content *</Label>
                    <Textarea
                      placeholder="Write post content..."
                      value={postContent}
                      onChange={(e) => setPostContent(e.target.value)}
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Mention Button */}
                  <div className="relative">
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => setShowMentionDropdown(!showMentionDropdown)}
                    >
                      <AtSign className="h-4 w-4 mr-2" />
                      @ Mention Page
                    </Button>
                    
                    {showMentionDropdown && filteredPagesForMention.length > 0 && (
                      <div className="absolute top-full left-0 mt-1 w-full bg-white border rounded-lg shadow-lg z-10 max-h-[200px] overflow-y-auto">
                        {filteredPagesForMention.map(page => (
                          <div
                            key={page.id}
                            className="flex items-center gap-2 p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleAddMention(page)}
                          >
                            {page.picture ? (
                              <img src={page.picture} alt="" className="w-6 h-6 rounded-full" />
                            ) : (
                              <FileText className="h-4 w-4 text-gray-400" />
                            )}
                            <span className="text-sm">@{page.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Mentioned Pages Display */}
                  {mentionedPages.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {mentionedPages.map(page => (
                        <div 
                          key={page.id}
                          className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm"
                        >
                          @{page.name}
                          <button 
                            onClick={() => handleRemoveMention(page.id)}
                            className="ml-1 hover:text-red-600"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <Button 
                    onClick={handleCreatePost} 
                    disabled={creatingPost || !postContent.trim()}
                    className="w-full"
                  >
                    {creatingPost ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Publishing...</>
                    ) : (
                      'Publish Post'
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 4: Publish Video */}
          <Card className={`${currentStep === 4 ? 'ring-2 ring-blue-500' : ''}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Video className="h-5 w-5 text-blue-600" />
                Step 4: Publish Video to Facebook Page
              </CardTitle>
              <CardDescription className="flex items-center gap-1">
                <HelpCircle className="h-4 w-4" />
                Upload and publish video content to your Page
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedPage ? (
                <div className="text-center py-6 text-gray-500">
                  <XCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a Facebook Page first</p>
                </div>
              ) : videoPublished ? (
                <div className="space-y-4">
                  {/* SUCCESS CONFIRMATION */}
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="font-bold text-green-800 flex items-center gap-2 text-lg">
                      <CheckCircle className="h-6 w-6" />
                      ✓ Video Published Successfully
                    </p>
                    {videoResult && (
                      <div className="mt-3 space-y-2">
                        <p className="text-green-700"><strong>Page:</strong> {videoResult.pageName}</p>
                        <p className="text-green-700"><strong>Title:</strong> {videoResult.title}</p>
                        <p className="text-green-600 text-sm">Video ID: {videoResult.videoId}</p>
                      </div>
                    )}
                  </div>
                  <Button variant="outline" onClick={() => { setVideoPublished(false); setVideoTitle(''); setVideoUrl(''); }}>
                    Upload Another Video
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700 flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Upload and publish video content to your Facebook Page.</span>
                  </div>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <Upload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Click to upload video or drag & drop</p>
                    <p className="text-xs text-gray-400 mt-1">MP4, MOV up to 100MB</p>
                    <Button variant="outline" className="mt-3">
                      Select Video
                    </Button>
                  </div>
                  
                  <div>
                    <Label>Video Title *</Label>
                    <Input
                      placeholder="Enter video title"
                      value={videoTitle}
                      onChange={(e) => setVideoTitle(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Video URL (optional)</Label>
                    <Input
                      placeholder="https://example.com/video.mp4"
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Target Page</Label>
                    <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                      {selectedPage.picture && (
                        <img src={selectedPage.picture} alt="" className="w-5 h-5 rounded-full" />
                      )}
                      {selectedPage.name}
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handlePublishVideo} 
                    disabled={publishingVideo || !videoTitle.trim()}
                    className="w-full"
                  >
                    {publishingVideo ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Publishing...</>
                    ) : (
                      <><Video className="h-4 w-4 mr-2" /> Publish Video</>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 5: Instagram Business Account */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Instagram className="h-5 w-5 text-pink-600" />
                Step 5: Instagram Business Account
              </CardTitle>
              <CardDescription className="flex items-center gap-1">
                <HelpCircle className="h-4 w-4" />
                Connect Instagram Business account to display account info
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-3 bg-pink-50 rounded-lg text-sm text-pink-700 flex items-start gap-2 mb-4">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>instagram_business_basic is used to connect and display Instagram Business account info.</span>
              </div>
              
              {!metaAuthenticated ? (
                <div className="text-center py-6 text-gray-500">
                  <XCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Connect to Facebook first to see Instagram accounts</p>
                </div>
              ) : loadingInstagram ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-8 w-8 animate-spin text-pink-600" />
                </div>
              ) : instagramAccount ? (
                <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-pink-200">
                  {/* SUCCESS CONFIRMATION */}
                  <div className="flex items-center gap-4">
                    {instagramAccount.profile_picture_url ? (
                      <img 
                        src={instagramAccount.profile_picture_url} 
                        alt="" 
                        className="w-16 h-16 rounded-full border-2 border-pink-300"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-pink-200 flex items-center justify-center">
                        <Instagram className="h-8 w-8 text-pink-600" />
                      </div>
                    )}
                    <div>
                      <p className="font-bold text-gray-900 text-xl">@{instagramAccount.username}</p>
                      <p className="text-sm text-gray-500">Account ID: {instagramAccount.id}</p>
                      {instagramAccount.followers_count && (
                        <p className="text-sm text-gray-500">{instagramAccount.followers_count} followers</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-green-600 font-bold text-lg">
                    <CheckCircle className="h-6 w-6" />
                    <span>✓ Status: Connected</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <Instagram className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No Instagram Business account linked</p>
                  <p className="text-sm mt-2">Link an Instagram Business account to your Facebook Page first</p>
                  <Button onClick={fetchInstagram} variant="outline" className="mt-4">
                    <RefreshCw className="h-4 w-4 mr-2" /> Check Again
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Screencast Guide */}
        <Card className="mt-6 bg-yellow-50 border-yellow-200">
          <CardHeader>
            <CardTitle className="text-yellow-800">🎬 Screencast Recording Guide</CardTitle>
          </CardHeader>
          <CardContent className="text-yellow-900">
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>Open browser and go to: <strong>https://www.automateseo.ai/meta-demo</strong></li>
              <li>Click "Continue with Facebook" button</li>
              <li>Facebook OAuth screen → approve permissions</li>
              <li>Redirect back to demo page</li>
              <li>Show Pages list</li>
              <li>Select Page</li>
              <li>Create post with @Page mention</li>
              <li>Publish post (success message visible)</li>
              <li>Upload video → publish</li>
              <li>Connect Instagram Business → show account info</li>
            </ol>
            <p className="mt-4 font-medium">
              ⚠️ Make sure all UI is in English and each action has a visible user click
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MetaDemoPage;
